import { VersionCategoryRaw } from "@/domain/VersionCategories";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import { Text, Tooltip } from "@fluentui/react-components";
import React, { useMemo } from "react";
import { Version } from "../../../../../../domain/Version";
import {
  OperationStatus,
  operationStatusVisuals,
  useOperationStatusMessages,
} from "../../../../helpers/DialogStatus";
import styles from "./index.module.css";

export type VersionUpdateStatus = {
  versionId: string;
  status: OperationStatus;
  error?: string;
};

type Props = {
  versions: Version[];
  versionCategories: VersionCategoryRaw[];
  selectedVersionIds: string[];
  updateStatuses: VersionUpdateStatus[];
};

const useTranslation = translation.use(nsVersion);

export const StatusList: React.FC<Props> = ({
  versions,
  versionCategories,
  selectedVersionIds,
  updateStatuses,
}) => {
  const { t } = useTranslation();
  const statusMessages = useOperationStatusMessages();

  const versionCategoryMap = useMemo(() => {
    return new Map(
      versionCategories.map((category) => [category.id, category.name])
    );
  }, [versionCategories]);

  return (
    <div className={styles.statusSection}>
      <div className={styles.sectionTitle}>
        {t.version.bulkUpdate.statusListTitle()}
      </div>
      <div className={styles.statusListWrapper}>
        <div className={styles.statusListHeader}>
          <div>{t.version.list.column.name()}</div>
          <div>{t.version.list.column.category()}</div>
          <div>{t.version.list.column.status()}</div>
        </div>
        <div>
          {selectedVersionIds.map((versionId) => {
            const version = versions.find((v) => v.id === versionId);
            const categoryName = version?.category
              ? versionCategoryMap.get(version.category) ?? ""
              : "";
            const status =
              updateStatuses.find((s) => s.versionId === versionId)?.status ??
              "waiting";
            const { Icon } = operationStatusVisuals[status];
            const statusMessage = statusMessages[status];
            const error = updateStatuses.find(
              (s) => s.versionId === versionId
            )?.error;

            return (
              <div key={versionId} className={styles.statusItem}>
                <Text>{version?.title}</Text>
                <Text>{categoryName}</Text>
                {status === "failed" ? (
                  <span className={styles[status]}>
                    <div className={styles.status}>
                      <Icon />
                      <Tooltip content={error || ""} relationship="description">
                        <div className={styles.hoverable}>{statusMessage}</div>
                      </Tooltip>
                    </div>
                  </span>
                ) : (
                  <span className={styles[status]}>
                    <div className={styles.status}>
                      <Icon />
                      <div>{statusMessage}</div>
                    </div>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
